// renderMarkdown — a minimal, SAFE markdown-to-React renderer for the in-app
// .md file reader (MarkdownReader). No external markdown library is in the
// project (and we don't add one), and we never use dangerouslySetInnerHTML —
// output is React nodes, exactly like shared/linkify.js ("Safe by
// construction").
//
// Scope: the engineering docs attached to ClientHub messages (e.g.
// GCP-DECOMMISSION-PART8-BACKEND-FOLLOWUPS.md). Covers the common block + inline
// elements those use and degrades unknown syntax to plain text rather than
// breaking:
//   blocks:  ATX headings (#..######), horizontal rules, fenced ``` code blocks,
//           blockquotes (> ), unordered (-, *, +) and ordered (1.) lists, GFM
//           tables (| a | b |), paragraphs, blank-line separation.
//   inline:  **bold**, *italic*, _italic_, `code`, [label](url), bare urls.
//
// Deliberately NOT full CommonMark/GFM — a reader for internal docs, not a
// general markdown engine. Anything unrecognised renders as readable text.
import React from "react";

const LINK_CLASS = "text-[#32B1FF] hover:underline break-all";

// One combined tokenizer so a single pass resolves all inline syntax. Branch
// order matters: `code` before markdown (so `**` inside `` ` `` is literal),
// `**bold**` before `*italic*`, links before bare urls.
const INLINE_RE =
  /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)|(\[[^\]]+\]\(https?:\/\/[^)\s]+\))|(https?:\/\/[^\s<]+)/g;

function renderInline(text, keyPrefix = "") {
  if (!text) return [];
  const nodes = [];
  let last = 0;
  let key = 0;
  let m;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("`")) {
      nodes.push(
        <code
          key={`${keyPrefix}-c-${key++}`}
          className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800 dark:bg-gray-800 dark:text-gray-200"
        >
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith("**")) {
      nodes.push(<strong key={`${keyPrefix}-b-${key++}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("*")) {
      nodes.push(<em key={`${keyPrefix}-i-${key++}`}>{tok.slice(1, -1)}</em>);
    } else if (tok.startsWith("_")) {
      nodes.push(<em key={`${keyPrefix}-u-${key++}`}>{tok.slice(1, -1)}</em>);
    } else if (tok.startsWith("[")) {
      const lm = tok.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
      if (lm) {
        nodes.push(
          <a key={`${keyPrefix}-l-${key++}`} href={lm[2]} target="_blank" rel="noreferrer" className={LINK_CLASS}>
            {lm[1]}
          </a>,
        );
      } else {
        nodes.push(tok);
      }
    } else {
      // Bare url — trim trailing punctuation like linkify does.
      let href = tok;
      let tail = "";
      if (/[.,;:!?)]$/.test(href)) {
        tail = href.slice(-1);
        href = href.slice(0, -1);
      }
      nodes.push(
        <a key={`${keyPrefix}-u-${key++}`} href={href} target="_blank" rel="noreferrer" className={LINK_CLASS}>
          {href}
        </a>,
      );
      if (tail) nodes.push(tail);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const HR_RE = /^(-{3,}|\*{3,}|_{3,})\s*$/;
const ULIST_RE = /^(\s*)([-*+])\s+(.*)$/;
const OLIST_RE = /^(\s*)(\d+)\.\s+(.*)$/;
const QUOTE_RE = /^>\s?(.*)$/;
const FENCE_RE = /^```/;

function parseTableRow(line) {
  return line.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
}
function isTableSeparator(line) {
  return /^\|?(\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/.test(line);
}

// Render a group of consecutive list lines, honouring indent depth for nesting.
function renderList(items, keyPrefix) {
  const root = { children: [], depth: -1, ordered: null };
  const stack = [root];
  for (let idx = 0; idx < items.length; idx++) {
    const raw = items[idx];
    const ul = raw.match(ULIST_RE);
    const ol = raw.match(OLIST_RE);
    const m = ul || ol;
    const depth = (m[1] || "").length;
    const ordered = !!ol;
    const content = m[3] || "";
    const node = { depth, ordered, content, children: [] };
    while (stack.length > 1 && stack[stack.length - 1].depth >= depth) stack.pop();
    stack[stack.length - 1].children.push(node);
    stack.push(node);
  }
  const toList = (nodes, kp) => {
    if (!nodes || nodes.length === 0) return null;
    // Group consecutive top-level nodes by their ordered flag so a switch from
    // `-` to `1.` starts a new <ul>/<ol> rather than merging into one list.
    const groups = [];
    for (const n of nodes) {
      const g = groups[groups.length - 1];
      if (g && g.ordered === n.ordered) g.items.push(n);
      else groups.push({ ordered: n.ordered, items: [n] });
    }
    return groups.map((grp, gi) => {
      const lis = grp.items.map((n, ni) => (
        <li key={`${kp}-${gi}-${ni}`}>
          {renderInline(n.content, `${kp}-${gi}-${ni}`)}
          {n.children && n.children.length > 0 ? toList(n.children, `${kp}-${gi}-${ni}`) : null}
        </li>
      ));
      return grp.ordered
        ? <ol key={`${kp}-g${gi}`} className="my-1.5 list-decimal space-y-1 pl-6">{lis}</ol>
        : <ul key={`${kp}-g${gi}`} className="my-1.5 list-disc space-y-1 pl-6">{lis}</ul>;
    });
  };
  return toList(root.children, keyPrefix);
}

/**
 * Render markdown text into an array of React block nodes.
 * @param {string} text
 * @returns {React.ReactNode[]}
 */
export function renderMarkdown(text) {
  if (!text || typeof text !== "string") return [];
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*$/.test(line)) { i++; continue; }

    // Fenced code block.
    if (FENCE_RE.test(line)) {
      const buf = [];
      i++;
      while (i < lines.length && !FENCE_RE.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      blocks.push(
        <pre key={`pre-${key++}`} className="my-2 overflow-x-auto rounded-lg bg-gray-900 p-3 text-sm text-gray-100 dark:bg-black/60">
          <code className="font-mono">{buf.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    if (HR_RE.test(line)) { blocks.push(<hr key={`hr-${key++}`} className="my-3 border-gray-200 dark:border-gray-800" />); i++; continue; }

    const hm = line.match(HEADING_RE);
    if (hm) {
      const level = Math.min(hm[1].length, 6);
      const sizes = ["text-2xl", "text-xl", "text-lg", "text-base", "text-sm", "text-sm"];
      blocks.push(
        React.createElement(`h${level}`, {
          key: `h-${key++}`,
          className: `mt-4 mb-1 font-semibold text-gray-900 dark:text-gray-100 ${sizes[level - 1]}`,
        }, renderInline(hm[2], `h-${key}`)),
      );
      i++; continue;
    }

    if (QUOTE_RE.test(line)) {
      const buf = [];
      while (i < lines.length && QUOTE_RE.test(lines[i])) { buf.push(lines[i].replace(QUOTE_RE, "$1")); i++; }
      blocks.push(
        <blockquote key={`bq-${key++}`} className="my-2 border-l-4 border-gray-200 pl-3 italic text-gray-600 dark:border-gray-700 dark:text-gray-400">
          {buf.map((b, bi) => <p key={`bq-${key}-${bi}`}>{renderInline(b)}</p>)}
        </blockquote>,
      );
      continue;
    }

    // Table (header + separator + rows, consecutive lines starting with |).
    if (line.startsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].startsWith("|")) { tableLines.push(lines[i]); i++; }
      if (tableLines.length >= 2 && isTableSeparator(tableLines[1])) {
        const headers = parseTableRow(tableLines[0]);
        const rows = tableLines.slice(2).map(parseTableRow);
        blocks.push(
          <div key={`tbl-${key++}`} className="my-3 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th key={hi} className="border border-gray-200 px-2 py-1 text-left font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-200">
                      {renderInline(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, ri) => (
                  <tr key={ri}>
                    {headers.map((_, ci) => (
                      <td key={ci} className="border border-gray-200 px-2 py-1 text-gray-700 dark:border-gray-700 dark:text-gray-300">
                        {renderInline(r[ci] || "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
        continue;
      }
      i -= tableLines.length; // not a real table — fall through to paragraph
    }

    if (ULIST_RE.test(line) || OLIST_RE.test(line)) {
      const items = [];
      while (i < lines.length && (ULIST_RE.test(lines[i]) || OLIST_RE.test(lines[i]))) { items.push(lines[i]); i++; }
      blocks.push(...renderList(items, `list-${key++}`));
      continue;
    }

    // Paragraph.
    const para = [];
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i]) &&
      !HEADING_RE.test(lines[i]) &&
      !HR_RE.test(lines[i]) &&
      !FENCE_RE.test(lines[i]) &&
      !QUOTE_RE.test(lines[i]) &&
      !ULIST_RE.test(lines[i]) &&
      !OLIST_RE.test(lines[i]) &&
      !lines[i].startsWith("|")
    ) { para.push(lines[i]); i++; }
    blocks.push(
      <p key={`p-${key++}`} className="my-1.5 leading-relaxed text-gray-800 dark:text-gray-200">
        {renderInline(para.join(" "), `p-${key}`)}
      </p>,
    );
  }

  return blocks;
}

export { renderInline };

